import { useCallback, useState, useEffect } from 'react';
import classNames from 'classnames';

import { Popover } from '@/mastodon/components/popover';
import TranslateIcon from '@/material-icons/400-24px/translate.svg?react';
import { Icon } from 'mastodon/components/icon';

const COMMANDS = [
  { id: '⋯⋯', label: '말줄임표⋯⋯' },
  { id: '*속닥{}', label: '속닥속닥' },
  { id: '*이텔릭{}', label: '이텔릭' },
  { id: '*볼드{}', label: '볼드체' },
  { id: '*강조볼드{}', label: '강조볼드체' },
  { id: '*밑줄{}', label: '밑줄긋기' },
  { id: '*블러{}', label: '블러효과' },
];

export const CommandDropdown: React.FC<{ 
  onSelect: (value: string) => void;
  text: string;
  onChange: (value: string) => void;
}> = ({ onSelect, text, onChange }) => {
  const [open, setOpen] = useState(false);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  
  const [activeTab, setActiveTab] = useState<'css' | 'spell' | 'sheet'>('css');
  
  const [spellResults, setSpellResults] = useState<any[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  const [sheetData, setSheetData] = useState<{stats: any, skills: {name: string, value: number}[]} | null>(null);
  const [skillSearchQuery, setSkillSearchQuery] = useState('');
  const [isFetchingSkills, setIsFetchingSkills] = useState(false);
  const [hasFetchedSkills, setHasFetchedSkills] = useState(false);
  const [hoveredSkill, setHoveredSkill] = useState<string | null>(null);

  const handleToggle = useCallback(() => setOpen(!open), [open]);
  const handleClose = useCallback(() => setOpen(false), []);

  const runSpellCheck = async () => {
    if (!text || text.trim() === '') return;
    setIsChecking(true);
    setHasChecked(true);
    try {
      const response = await fetch('/speller/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text })
      });
      const data = await response.json();
      const errors = data.suggestions || data;
      setSpellResults(Array.isArray(errors) ? errors : []);
    } catch (error) {
      console.error(error);
      setSpellResults([]);
    } finally {
      setIsChecking(false);
    }
  };

  const fetchSkills = async () => {
    setIsFetchingSkills(true);
    setHasFetchedSkills(true);
    try {
      const response = await fetch('/my_inventory/skills');
      if (response.ok) {
        const data = await response.json();
        setSheetData(data.error ? null : data);
      }
    } catch (error) {
      console.error(error);
      setSheetData(null);
    } finally {
      setIsFetchingSkills(false);
    }
  };

  useEffect(() => {
    if (open && activeTab === 'spell' && !hasChecked) {
      runSpellCheck();
    } else if (open && activeTab === 'sheet' && !hasFetchedSkills) {
      fetchSkills(); 
    }
  }, [open, activeTab, hasChecked, hasFetchedSkills, text]);

  useEffect(() => {
    if (!open) {
      setHasChecked(false);
      setSpellResults([]);
      setSkillSearchQuery(''); 
      setActiveTab('css');
    }
  }, [open]);

  const handleReplaceSingle = (err: any, index: number) => {
    const original = err.original || err.token;
    const suggestion = (err.suggestions && err.suggestions[0]) || err.checked || err.suggestion;
    if (original && suggestion) {
       const correctedText = text.replaceAll(original, suggestion);
       onChange(correctedText);
    }
    const newResults = spellResults.filter((_, i) => i !== index);
    setSpellResults(newResults);
  };

  const handleReplaceAll = () => {
    if (spellResults.length === 0) return;
    let correctedText = text;
    spellResults.forEach(err => {
      const original = err.original || err.token;
      const suggestion = (err.suggestions && err.suggestions[0]) || err.checked || err.suggestion;
      if (original && suggestion) {
         correctedText = correctedText.replaceAll(original, suggestion);
      }
    });
    onChange(correctedText);
    handleClose(); 
  };

  const handleDiceCommand = (command: string) => {
    // 1. 입력창에 이미 @dice 텍스트가 있는지 확인
    const hasDiceInText = text.includes('@dice');
    
    // 2. 화면에 마스토돈 답글 표시줄(.reply-indicator)이 있는지, 
    //    그리고 그 답글 대상에 'dice'가 포함되어 있는지 확인
    const replyIndicator = document.querySelector('.reply-indicator');
    const isReplyingToDice = replyIndicator && replyIndicator.textContent?.toLowerCase().includes('dice');

    // 이미 입력창에 있거나, 주사위 봇에게 답글을 작성 중이면 @dice 생략
    if (hasDiceInText || isReplyingToDice) {
      onSelect(command);
    } else {
      onSelect(`@dice ${command}`);
    }
    handleClose();
  };

  return (
    <>
      <button
        type='button'
        ref={setTargetElement}
        title="문자 포맷 및 기능치 시트"
        aria-expanded={open}
        onClick={handleToggle}
        className={classNames('dropdown-button', { active: open })}
      >
        <Icon id='translate' icon={TranslateIcon} />
        <span className='dropdown-button__label'>문자</span>
      </button>

      <Popover isOpen={open} onClose={handleClose} offset={5} reference={targetElement}>
        {({ props, placement }) => (
          <div {...props}>
            <div className={`dropdown-animation language-dropdown__dropdown ${placement}`} style={{ width: '320px' }}>
              
              <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border-primary)' }}>
                <button
                  onClick={(e) => { e.preventDefault(); setActiveTab('css'); }}
                  style={{ 
                    flex: 1, padding: '12px 6px', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px',
                    background: activeTab === 'css' ? 'var(--color-bg-brand-base)' : 'transparent', 
                    color: activeTab === 'css' ? 'var(--color-text-on-brand-base)' : 'var(--color-text-light)' 
                  }}
                >
                  포맷
                </button>
                <button
                  onClick={(e) => { e.preventDefault(); setActiveTab('spell'); }}
                  style={{ 
                    flex: 1, padding: '12px 6px', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px',
                    background: activeTab === 'spell' ? 'var(--color-bg-brand-base)' : 'transparent', 
                    color: activeTab === 'spell' ? 'var(--color-text-on-brand-base)' : 'var(--color-text-light)' 
                  }}
                >
                  맞춤법
                </button>
                <button
                  onClick={(e) => { e.preventDefault(); setActiveTab('sheet'); }}
                  style={{ 
                    flex: 1, padding: '12px 6px', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px',
                    background: activeTab === 'sheet' ? 'var(--color-bg-brand-base)' : 'transparent', 
                    color: activeTab === 'sheet' ? 'var(--color-text-on-brand-base)' : 'var(--color-text-light)' 
                  }}
                >
                  시트
                </button>
              </div>

              <div className='language-dropdown__dropdown__results emoji-mart-scroll' style={{ maxHeight: '420px', padding: activeTab === 'spell' ? '12px' : '0', display: 'flex', flexDirection: 'column' }}>
                
                {activeTab === 'css' && (
                  COMMANDS.map((cmd) => {
                    const customClass = cmd.id.substring(cmd.id.indexOf('*') + 1, cmd.id.indexOf('{'));
                    return (
                      <div
                        key={cmd.id}
                        role='option'
                        tabIndex={0}
                        className='language-dropdown__dropdown__results__item'
                        onClick={(e) => {
                          e.preventDefault();
                          onSelect(cmd.id);
                          handleClose();
                        }}
                      >
                        <span className='language-dropdown__dropdown__results__item__native-name'>
                          <span className={customClass}>{cmd.label}</span>
                        </span>
                      </div>
                    );
                  })
                )}

                {activeTab === 'spell' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {isChecking ? (
                      <div style={{ textAlign: 'center', padding: '30px 10px', color: 'var(--color-text-light)', fontSize: '13px' }}>
                        검사 중...
                      </div>
                    ) : spellResults.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '30px 10px', color: 'var(--color-text-light)', fontSize: '13px', lineHeight: '1.4' }}>
                        발견된 맞춤법 오류가 없거나,<br/>검사할 내용이 없습니다.
                      </div>
                    ) : (
                      <>
                        {spellResults.map((err, idx) => {
                          const original = err.original || err.token;
                          const suggestion = (err.suggestions && err.suggestions[0]) || err.checked || err.suggestion;
                          
                          const infoText = err.info || '';
                          const infoParts = infoText.split('\n\n\n\n');
                          const explanation = infoParts[0]; 
                          const example = infoParts.length > 1 ? infoParts.slice(1).join(' ') : ''; 

                          return (
                            <div key={idx} style={{ paddingBottom: '12px', borderBottom: '1px dashed var(--color-border-primary)' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                <div>
                                  <div style={{ fontSize: '14px', color: 'var(--color-text-brand)', textDecoration: 'line-through', marginBottom: '4px' }}>
                                    {original}
                                  </div>
                                  <div style={{ fontSize: '15px', color: 'var(--color-text-brand)', fontWeight: 'bold' }}>
                                    {suggestion}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleReplaceSingle(err, idx)}
                                  style={{ 
                                    padding: '6px 12px', 
                                    background: 'var(--color-bg-brand-base)', 
                                    color: 'var(--color-text-on-brand-base)', 
                                    border: 'none', 
                                    borderRadius: '4px', 
                                    fontWeight: 'bold', 
                                    cursor: 'pointer', 
                                    fontSize: '12px',
                                    flexShrink: 0
                                  }}
                                >
                                  대치
                                </button>
                              </div>

                              <div style={{ fontSize: '12px', color: 'var(--color-text-light)', lineHeight: '1.5', whiteSpace: 'pre-wrap', backgroundColor: 'var(--color-bg-base)', padding: '8px', borderRadius: '4px' }}>
                                <span>{explanation}</span>
                                {example && (
                                  <span style={{ display: 'block', marginTop: '8px', color: '#888' }}>
                                    예) {example}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        
                        <button
                          type="button"
                          onClick={handleReplaceAll}
                          style={{ 
                            width: '100%', padding: '12px', 
                            background: 'var(--color-bg-brand-base)', color: 'var(--color-text-on-brand-base)', 
                            border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', marginTop: '5px' 
                          }}
                        >
                          모두 대치하기
                        </button>
                      </>
                    )}
                  </div>
                )}

                {activeTab === 'sheet' && (
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {isFetchingSkills ? (
                      <div style={{ textAlign: 'center', padding: '30px 10px', color: 'var(--color-text-light)', fontSize: '13px' }}>
                        시트 정보를 불러오는 중...
                      </div>
                    ) : !sheetData || !sheetData.skills ? (
                      <div style={{ textAlign: 'center', padding: '30px 10px', color: 'var(--color-text-light)', fontSize: '13px' }}>
                        시트 정보가 없습니다.
                      </div>
                    ) : (
                      <>
                        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border-primary)', backgroundColor: 'var(--color-bg-base)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--color-text-primary)' }}>
                            체력 : <span style={{ color: 'var(--color-text-brand)' }}>{sheetData.stats.hp_current}</span> / {sheetData.stats.hp_max}
                          </div>
                          
                          <div 
                            onMouseEnter={() => setHoveredSkill('이성_스탯')}
                            onMouseLeave={() => setHoveredSkill(null)}
                            style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', minHeight: '24px' }}
                          >
                            <span style={{ marginRight: '8px' }}>
                              이성 : <span style={{ color: 'var(--color-text-brand)' }}>{sheetData.stats.sanity}</span> / {sheetData.stats.sanity_max}
                            </span>
                            {hoveredSkill === '이성_스탯' && (
                              <button 
                                type="button" 
                                title="판정"
                                onClick={(e) => { e.preventDefault(); handleDiceCommand('[판정/이성]'); }}
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center' }}
                              >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                  <circle cx="8.5" cy="8.5" r="1.5" fill="var(--color-text-brand)"></circle>
                                  <circle cx="15.5" cy="15.5" r="1.5" fill="var(--color-text-brand)"></circle>
                                  <circle cx="15.5" cy="8.5" r="1.5" fill="var(--color-text-brand)"></circle>
                                  <circle cx="8.5" cy="15.5" r="1.5" fill="var(--color-text-brand)"></circle>
                                </svg>
                              </button>
                            )}
                          </div>

                          <div style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--color-text-primary)' }}>
                            허기 : <span style={{ color: 'var(--color-text-brand)' }}>{sheetData.stats.hunger}</span>
                          </div>
                        </div>

                        <div style={{ padding: '10px 0px', borderBottom: '1px solid var(--color-border-primary)' }}>
                          <form 
                            role="search" 
                            className="search" 
                            onSubmit={(e) => e.preventDefault()}
                            style={{ margin: 0 }}
                          >
                            <input
                              className="search__input"
                              type="text"
                              placeholder="기능치 검색..."
                              value={skillSearchQuery}
                              onChange={(e) => setSkillSearchQuery(e.target.value)}
                            />
                            <div className="search__icon-wrapper">
                              <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24" className="icon icon-search search__icon" aria-hidden="true">
                                <path d="M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Z"></path>
                              </svg>
                              {skillSearchQuery.length > 0 && (
                                <button 
                                  type="button" 
                                  className="search__icon search__icon--clear-button" 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSkillSearchQuery('');
                                  }}
                                  title="검색 초기화" 
                                  aria-label="검색 초기화"
                                  style={{ pointerEvents: 'auto', cursor: 'pointer', zIndex: 10 }}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24" className="icon icon-times-circle" aria-hidden="true">
                                    <path d="m336-280 144-144 144 144 56-56-144-144 144-144-56-56-144 144-144-144-56 56 144 144-144 144 56 56ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z"></path>
                                  </svg>
                                </button>
                              )}
                            </div>
                          </form>
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto' }}>
                          {sheetData.skills
                            .filter(s => s.name.toLowerCase().includes(skillSearchQuery.toLowerCase()))
                            .map((s, idx) => (
                              <div 
                                key={idx}
                                onMouseEnter={() => setHoveredSkill(s.name)}
                                onMouseLeave={() => setHoveredSkill(null)}
                                style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  padding: '8px 16px', 
                                  borderBottom: '1px solid var(--color-border-primary)',
                                  minHeight: '36px',
                                  boxSizing: 'border-box'
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', minHeight: '20px' }}>
                                  <span style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--color-text-primary)' }}>
                                    {s.name}
                                  </span>
                                  {hoveredSkill === s.name && (
                                    <div style={{ display: 'flex', gap: '6px', marginLeft: '8px' }}>
                                      <button 
                                        type="button" 
                                        title="탐색"
                                        onClick={(e) => { e.preventDefault(); handleDiceCommand(`[탐색/${s.name}]`); }}
                                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center' }}
                                      >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <circle cx="11" cy="11" r="8"></circle>
                                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                        </svg>
                                      </button>
                                      <button 
                                        type="button" 
                                        title="판정"
                                        onClick={(e) => { e.preventDefault(); handleDiceCommand(`[판정/${s.name}]`); }}
                                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center' }}
                                      >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                          <circle cx="8.5" cy="8.5" r="1.5" fill="var(--color-text-brand)"></circle>
                                          <circle cx="15.5" cy="15.5" r="1.5" fill="var(--color-text-brand)"></circle>
                                          <circle cx="15.5" cy="8.5" r="1.5" fill="var(--color-text-brand)"></circle>
                                          <circle cx="8.5" cy="15.5" r="1.5" fill="var(--color-text-brand)"></circle>
                                        </svg>
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <span style={{ fontSize: '15px', fontWeight: 'bold', color: 'var(--color-text-brand)' }}>
                                  {s.value}
                                </span>
                              </div>
                            ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
                
              </div>
            </div>
          </div>
        )}
      </Popover>
    </>
  );
};